import { useState } from "react";
import { createDevSuggestion } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { BookOpen, Briefcase, Award, Users, GraduationCap, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { TrainingSuggestionIntegrations } from "./IntegrationActions";

const RESOURCE_TYPES = [
  { v: "course", label: "Eğitim/Kurs", icon: BookOpen },
  { v: "project", label: "Proje", icon: Briefcase },
  { v: "certification", label: "Sertifika", icon: Award },
  { v: "coaching", label: "Koçluk", icon: Users },
  { v: "book", label: "Kitap", icon: BookOpen },
];

// Resource-type-aware form labels & placeholders (gelişim önerisi UI metni)
const TYPE_CONFIG = {
  course: {
    titleLabel: "Eğitim Adı *",
    titlePh: "Örn. Coursera — Machine Learning Specialization",
    providerLabel: "Sağlayıcı",
    providerPh: "Coursera / İç Akademi / vb.",
    showDuration: true,
    durationLabel: "Süre (hafta)",
  },
  project: {
    titleLabel: "Proje Adı *",
    titlePh: "Örn. Risk Modeli Modernizasyonu — Q3 İç Proje",
    providerLabel: "Sponsor Birim",
    providerPh: "Risk Yönetimi / Veri ve Analitik / vb.",
    showDuration: true,
    durationLabel: "Süre (hafta)",
  },
  certification: {
    titleLabel: "Sertifika Adı *",
    titlePh: "Örn. PMP / CFA Level 1 / AWS Certified Solutions Architect",
    providerLabel: "Veren Kurum",
    providerPh: "PMI / CFA Institute / AWS / vb.",
    showDuration: true,
    durationLabel: "Hazırlık Süresi (hafta)",
  },
  coaching: {
    titleLabel: "Koçluk Programı *",
    titlePh: "Örn. Executive Coaching — Liderlik Geçişi",
    providerLabel: "Koç / Sağlayıcı",
    providerPh: "İsim Soyisim / Koçluk Firması",
    showDuration: true,
    durationLabel: "Süre (hafta)",
  },
  book: {
    titleLabel: "Kitap Adı *",
    titlePh: "Örn. Atomic Habits — James Clear",
    providerLabel: "Yazar",
    providerPh: "Örn. Daniel Kahneman",
    showDuration: false,
    durationLabel: "Okuma Süresi (hafta)",
  },
};

export default function SuggestTrainingDrawer({ open, onOpenChange, employee, suggesterId, onSuggested }) {
  const [resourceType, setResourceType] = useState("course");
  const [title, setTitle] = useState("");
  const [provider, setProvider] = useState("");
  const [url, setUrl] = useState("");
  const [skillName, setSkillName] = useState("");
  const [duration, setDuration] = useState(6);
  const [priority, setPriority] = useState("medium");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setResourceType("course");
    setTitle("");
    setProvider("");
    setUrl("");
    setSkillName("");
    setDuration(6);
    setPriority("medium");
    setMessage("");
  };

  const handleSubmit = async () => {
    if (!title.trim() || !skillName.trim()) {
      toast.error("Beceri ve başlık zorunlu");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await createDevSuggestion({
        employee_id: employee.id,
        suggested_by_id: suggesterId,
        skill_name: skillName,
        resource_type: resourceType,
        title,
        provider,
        url,
        duration_weeks: Number(duration) || 4,
        message,
        priority,
      });
      toast.success("Gelişim önerisi gönderildi", {
        description: `${employee.name} talebini değerlendirecek.`,
      });
      onSuggested?.(resp.suggestion);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error("Öneri gönderilemedi");
    } finally {
      setSubmitting(false);
    }
  };

  if (!employee) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-white sm:max-w-xl w-full overflow-y-auto p-0 border-l border-slate-200" data-testid="suggest-training-drawer">
        <div className="px-8 pt-8 pb-6 bg-gradient-to-br from-amber-50 via-white to-white border-b border-slate-200">
          <SheetHeader className="text-left">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                <Sparkles className="w-3.5 h-3.5 text-amber-700" />
              </div>
              <span className="label-eyebrow text-amber-700">Gelişim Önerisi Gönder</span>
            </div>
            <div className="flex items-start gap-4">
              <Avatar className="w-14 h-14 ring-2 ring-amber-500/30">
                <AvatarImage src={employee.avatar} />
                <AvatarFallback>{employee.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <SheetTitle className="font-display text-xl font-semibold">{employee.name}</SheetTitle>
                <SheetDescription className="text-sm text-slate-600">
                  {employee.title} · {employee.department}
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>
        </div>

        <div className="px-8 py-6 space-y-5">
          <div>
            <Label className="text-sm font-medium text-slate-800 mb-2 block">Kaynak Türü</Label>
            <RadioGroup value={resourceType} onValueChange={setResourceType} className="grid grid-cols-5 gap-2">
              {RESOURCE_TYPES.map((rt) => (
                <label
                  key={rt.v}
                  htmlFor={`rt-${rt.v}`}
                  className={`flex flex-col items-center gap-1 p-2 rounded-lg border cursor-pointer transition ${
                    resourceType === rt.v ? "border-amber-500 bg-amber-50" : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                >
                  <RadioGroupItem value={rt.v} id={`rt-${rt.v}`} className="sr-only" />
                  <rt.icon className={`w-4 h-4 ${resourceType === rt.v ? "text-amber-700" : "text-slate-500"}`} />
                  <span className="text-[0.65rem] text-center">{rt.label}</span>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-700 mb-1.5 block">Beceri *</Label>
              <Input
                data-testid="suggest-skill"
                value={skillName}
                onChange={(e) => setSkillName(e.target.value)}
                placeholder="Örn. Yapay Zeka ve LLM"
                className="bg-white"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-700 mb-1.5 block">{TYPE_CONFIG[resourceType].durationLabel}</Label>
              <Input
                data-testid="suggest-duration"
                type="number"
                value={duration}
                min={1}
                max={52}
                onChange={(e) => setDuration(e.target.value)}
                className="bg-white"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-700 mb-1.5 block">{TYPE_CONFIG[resourceType].titleLabel}</Label>
            <Input
              data-testid="suggest-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={TYPE_CONFIG[resourceType].titlePh}
              className="bg-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-700 mb-1.5 block">{TYPE_CONFIG[resourceType].providerLabel}</Label>
              <Input
                data-testid="suggest-provider"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                placeholder={TYPE_CONFIG[resourceType].providerPh}
                className="bg-white"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-700 mb-1.5 block">Link (opsiyonel)</Label>
              <Input
                data-testid="suggest-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
                className="bg-white"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-700 mb-1.5 block">Öncelik</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="suggest-priority" className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="high">Yüksek — şimdi başla</SelectItem>
                <SelectItem value="medium">Orta — 30 gün içinde</SelectItem>
                <SelectItem value="low">Düşük — fırsat olursa</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-slate-700 mb-1.5 block">Mesajın (Neden bu kaynağı öneriyorsun?)</Label>
            <Textarea
              data-testid="suggest-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Çalışana bağlam ver. Bu kaynağı neden seçtin, hangi gap'i kapatacak..."
              rows={3}
              className="bg-white resize-none"
            />
          </div>

          <Button
            data-testid="suggest-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Send className="w-4 h-4 mr-2" />
            {submitting ? "Gönderiliyor…" : "Öneriyi gönder"}
          </Button>

          {employee && title && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2">
              <div className="text-[0.65rem] uppercase tracking-wider font-semibold text-slate-500">
                Outlook ile birlikte gönder (opsiyonel)
              </div>
              <TrainingSuggestionIntegrations
                employeeName={employee.name}
                title={title || "Öneri"}
                suggester="Yönetici"
              />
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
